import React, { useState, useMemo, Suspense, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pagination, Icon, Message, Dropdown } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from '../../../store/store';
import { useToasts } from 'common/toast';
import { teamApi } from '../api/teamApi';
import TeamOrgStructure from './TeamOrgStructure';
import { getCurrentOrganizationId, isCurrentUserStaffOrSuperAdmin } from '../../../common/selectors/auth';
import { AppConfig } from '../../../Config';
import { authenticatedFetch } from '../../../common/utils/authFetch';
import { PositionedModal } from '../../../common/components/PositionedModal';
import { StyledArchiveButton, StyledCancelButton, StyledDeleteButton } from 'common/components/button/StyledButton';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { StyledAddButton } from 'common/components/button/StyledButton';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { useGetAllTeamsQuery, useDeleteTeamMutation, useArchiveTeamMutation, useUnarchiveTeamMutation, useRebuildTeamMembersMutation, useGenerateTeamExternalIdsMutation } from '../api/teamApi';
import { Team } from '../types';
import styled from 'styled-components';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import Tabs from 'common/components/Tabs';
import TeamMembersModal from './TeamMembersModal';
import { TeamEditModal } from './TeamEditModal';
import ImportTeamModal from './ImportTeamModal';

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  min-width: 300px;
`;

const StatusSelect = styled.select`
  height: 36px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  padding: 0 10px;
  min-width: 180px;
  background: #fff;
`;

const ActionCell = styled(TableCell)`
  gap: 8px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const TeamManagement: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const dispatch = useDispatch<AppDispatch>();
  const prefetchTeamDetail = (id: number) => {
    // Aggressive prefetch: force a fresh fetch so the modal will have current data when it opens
    dispatch(teamApi.util.prefetch('getTeamDetail', id, { force: true }));
  };
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived'>('active');
  const [organizationFilter, setOrganizationFilter] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'org'>('org');
  const [selectedOrgTeam, setSelectedOrgTeam] = useState<Team | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const PAGE_SIZE = 9;

  const { data: teamsData, isLoading, error, refetch } = useGetAllTeamsQuery({ status: statusFilter });

  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [membersModalTeamId, setMembersModalTeamId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalTeam, setEditModalTeam] = useState<Team | null>(null);
  const [deleteConfirmTeam, setDeleteConfirmTeam] = useState<Team | null>(null);
  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
  const [deleteTeam, { isLoading: isDeleting }] = useDeleteTeamMutation();
  const [archiveTeam, { isLoading: isArchiving }] = useArchiveTeamMutation();
  const [unarchiveTeam, { isLoading: isUnarchiving }] = useUnarchiveTeamMutation();
  const [rebuildTeamMembers, { isLoading: isRebuilding }] = useRebuildTeamMembersMutation();
  const [generateTeamExternalIds, { isLoading: isGeneratingExternalIds }] = useGenerateTeamExternalIdsMutation();
  const [importTeamModalOpen, setImportTeamModalOpen] = useState(false);

  const [sortConfig, setSortConfig] = useState<{
    key: keyof Team;
    direction: 'asc' | 'desc';
  }>({
    key: 'name',
    direction: 'asc',
  });
  const [isDownloadingExport, setIsDownloadingExport] = useState(false);
  const isMutatingTeam = isDeleting || isArchiving || isUnarchiving;

  const currentOrganizationId = useSelector(getCurrentOrganizationId);
  const isCurrentUserStaffOrSuperAdminFlag = useSelector(isCurrentUserStaffOrSuperAdmin);
  const { addToast } = useToasts();

  const openRebuildTeamMembersConfirm = () => {
    if (!currentOrganizationId) {
      addToast('No organization selected.', { appearance: 'error', autoDismiss: true });
      return;
    }
    setRebuildConfirmOpen(true);
  };

  const handleGenerateTeamExternalIds = async () => {
    if (!currentOrganizationId) {
      addToast('No organization selected.', { appearance: 'error', autoDismiss: true });
      return;
    }

    try {
      const result = await generateTeamExternalIds().unwrap();
      const updatedTeams = Number(result?.updated_teams || 0);
      if (updatedTeams > 0) {
        addToast(`Generated external IDs for ${updatedTeams} team${updatedTeams === 1 ? '' : 's'}.`, {
          appearance: 'success',
          autoDismiss: true,
        });
      } else {
        addToast('All teams already have external IDs.', {
          appearance: 'success',
          autoDismiss: true,
        });
      }
      refetch();
    } catch (err) {
      console.error('Failed to generate team external IDs:', err);
      addToast('Failed to generate team external IDs.', { appearance: 'error', autoDismiss: true });
    }
  };

  const confirmRebuildTeamMembers = async () => {
    setRebuildConfirmOpen(false);
    try {
      await rebuildTeamMembers().unwrap();
      addToast('Team memberships rebuilt successfully.', { appearance: 'success', autoDismiss: true });
      refetch();
    } catch (err) {
      console.error('Failed to rebuild team memberships:', err);
      addToast('Failed to rebuild team memberships.', { appearance: 'error', autoDismiss: true });
    }
  };

  const handleExportTeams = async () => {
    if (!currentOrganizationId) {
      addToast('No organization selected for export.', { appearance: 'error', autoDismiss: true });
      return;
    }

    try {
      setIsDownloadingExport(true);
      const baseUrl = AppConfig.api.apiUrl;
      const endpoint = '/api/portal/v1/teams/export/';
      const url = `${baseUrl}${endpoint}`;

      const response = await authenticatedFetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-NULOGIC-ORGANIZATION-ID': currentOrganizationId.toString(),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to download team export: ${response.status} ${response.statusText} ${errorText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `team_export_${currentOrganizationId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      addToast('Team export downloaded successfully.', { appearance: 'success', autoDismiss: true });
    } catch (err) {
      console.error('Failed to download team export:', err);
      addToast('Failed to download team export. Please try again.', { appearance: 'error', autoDismiss: true });
    } finally {
      setIsDownloadingExport(false);
    }
  };

  const parentTeamQuery = searchParams.get('parent_team');
  const parentTeamId = parentTeamQuery ? Number(parentTeamQuery) : undefined;

  const setParentTeamQuery = (team: Team | null) => {
    const nextParams = new URLSearchParams(searchParams);
    if (team) {
      nextParams.set('parent_team', String(team.id));
    } else {
      nextParams.delete('parent_team');
    }
    setSearchParams(nextParams, { replace: true });
    setSelectedOrgTeam(team);
  };

  useEffect(() => {
    if (!teamsData?.results || parentTeamId == null || Number.isNaN(parentTeamId)) {
      setSelectedOrgTeam(null);
      return;
    }

    const foundTeam = teamsData.results.find((team) => team.id === parentTeamId) || null;
    setSelectedOrgTeam(foundTeam);
  }, [teamsData, parentTeamId]);

  const handleSort = (key: keyof Team) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const filteredTeams = useMemo(() => {
    if (!teamsData?.results) return [];

    const lowerName = nameFilter.trim().toLowerCase();

    const filtered = teamsData.results.filter((team) => {
      const matchesName = !lowerName || team.name?.toLowerCase().includes(lowerName);
      const matchesOrg = !organizationFilter || team.organization === organizationFilter;
      return matchesName && matchesOrg;
    });

    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [teamsData, nameFilter, organizationFilter, sortConfig, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const paginatedTeams = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredTeams.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredTeams, currentPage]);

  const totalPages = useMemo(
    () => Math.ceil(filteredTeams.length / PAGE_SIZE),
    [filteredTeams]
  );

  const handleDelete = async (teamId: number) => {
    // Open the delete confirmation modal (find team details to display)
    const teamToDelete = teamsData?.results?.find(t => t.id === teamId) || paginatedTeams.find(t => t.id === teamId) || { id: teamId, name: 'this team' } as Team;
    setDeleteConfirmTeam(teamToDelete);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmTeam) return;
    const shouldUnarchive = Boolean(deleteConfirmTeam.is_archived);
    const shouldArchive = Boolean(deleteConfirmTeam.has_snapshot_data);

    try {
      if (shouldUnarchive) {
        await unarchiveTeam(deleteConfirmTeam.id).unwrap();
        addToast('Team unarchived successfully.', { appearance: 'success', autoDismiss: true });
      } else if (shouldArchive) {
        await archiveTeam(deleteConfirmTeam.id).unwrap();
        addToast('Team archived successfully.', { appearance: 'success', autoDismiss: true });
      } else {
        await deleteTeam(deleteConfirmTeam.id).unwrap();
        addToast('Team deleted successfully.', { appearance: 'success', autoDismiss: true });
      }
      setDeleteConfirmTeam(null);
      refetch();
    } catch (error) {
      const action = shouldUnarchive ? 'unarchive' : shouldArchive ? 'archive' : 'delete';
      console.error(`Failed to ${action} team:`, error);
      alert(`Failed to ${action} team. Please try again.`);
    }
  };

  if (isLoading) {
    return (<LoadingSpinner />);
  }

  if (error) {
    return (
      <PageContent>
        <PageSection>
          <Message negative>Error loading teams. Please try again.</Message>
        </PageSection>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <PageSection>
        <PageHeader
          title="Team Management"
          controls={
            <>
              <StyledAddButton label="New Team" height='34px' onClick={() => {
                  // If the user is currently in List view, open the create modal inline
                  // instead of navigating away to the create route so we preserve the current tab.
                  if (viewMode === 'list') {
                    setEditModalTeam(null);
                    setEditModalOpen(true);
                    return;
                  }
                  const route = parentTeamQuery ? `/app/teams/create?parent_team=${parentTeamQuery}` : '/app/teams/create';
                  navigate(route);
                }} />
              {isCurrentUserStaffOrSuperAdminFlag && (
                <Dropdown
                  button
                  icon="setting"
                  floating
                  className="icon"
                  data-testid="team-management-actions-dropdown"
                  style={{ width: '38px', height: '35px', borderRadius: '8px', zIndex: 1000 }}
                  direction="left"
                  pointing="top right"
                >
                  <Dropdown.Menu>
                    <Dropdown.Item
                      icon="download"
                      text={isDownloadingExport ? 'Downloading...' : 'Team Export'}
                      data-testid="team-export-menu-item"
                      disabled={isDownloadingExport}
                      onClick={() => {
                        handleExportTeams();
                      }}
                    />
                    <Dropdown.Item
                      icon="cloud upload"
                      text="Team Import / Update"
                      onClick={() => {
                        setImportTeamModalOpen(true);
                      }}
                    />
                    <Dropdown.Divider />
                    <Dropdown.Item
                      icon="hashtag"
                      text={isGeneratingExternalIds ? 'Generating External IDs...' : 'Generate Missing External IDs'}
                      disabled={isGeneratingExternalIds}
                      onClick={() => {
                        handleGenerateTeamExternalIds();
                      }}
                    />
                    <Dropdown.Item
                      icon="refresh"
                      text={isRebuilding ? 'Rebuilding Team Members...' : 'Rebuild Team Members'}
                      disabled={isRebuilding}
                      onClick={() => {
                        openRebuildTeamMembersConfirm();
                      }}
                    />
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </>
          }
        />
      <PageSection>
              <Tabs
                options={["Org Chart", "Team List"]}
                selected={viewMode === 'list' ? 1 : 0}
                onSelect={(index: number) => setViewMode(index === 0 ? 'org' : 'list')}
                borderbottom
              />
      </PageSection>

        {viewMode === 'org' ? (
          <div style={{ marginTop: 8 }}>
            <Suspense fallback={<div>Loading org chart...</div>}>
              <TeamOrgStructure
                onTeamSelect={(team) => navigate(`/app/teams/${team.id}`)}
                onSelectedTeamChange={setParentTeamQuery}
                initialSelectedTeamId={parentTeamQuery || undefined}
              />
            </Suspense>
          </div>
        ) : filteredTeams.length === 0 ? (
          <Message>
            {nameFilter || organizationFilter
              ? 'No teams found matching your filters.'
              : 'No teams found.'}
          </Message>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <HeaderCell selectable onClick={() => handleSort('name')} width="4">
                    Team Name{' '}
                    {sortConfig.key === 'name' && (
                      <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                    )}
                  </HeaderCell>
                  <HeaderCell selectable onClick={() => handleSort('leader_name')} width="4">
                    Leader{' '}
                    {sortConfig.key === 'leader_name' && (
                      <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                    )}
                  </HeaderCell>
                  <HeaderCell selectable onClick={() => handleSort('parent_team_name')} width="4">
                    Parent Team{' '}
                    {sortConfig.key === 'parent_team_name' && (
                      <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                    )}
                  </HeaderCell>
                  <HeaderCell justify='center' selectable onClick={() => handleSort('member_count')} width="2">
                    Members{' '}
                    {sortConfig.key === 'member_count' && (
                      <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                    )}
                  </HeaderCell>
                  <HeaderCell width="2" justify="center">Actions</HeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTeams.map((team) => (
                  <TableRow
                    key={team.id}
                    $selectable
                    onClick={() => {
                      // Prefetch team detail so the Manage Members modal opens with cached data
                      try {
                        // dispatch a prefetch action for RTK Query
                        // eslint-disable-next-line @typescript-eslint/no-use-before-define
                        prefetchTeamDetail(team.id);
                      } catch (e) {
                        // ignore - prefetch best-effort
                      }
                      setMembersModalTeamId(team.id);
                      setMembersModalOpen(true);
                    }}
                  >
                    <TableCell $firstcol>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {team.color && (
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '4px',
                              background: team.color,
                              border: '1px solid #e0e0e0',
                              display: 'inline-block',
                            }}
                          />
                        )}
                        {team.name}
                      </div>
                    </TableCell>
                    <TableCell>{team.leader_name || team.leader_email || '—'}</TableCell>
                    <TableCell>{team.parent_team_name || '—'}</TableCell>
                    <TableCell justify="center">{team.member_count ?? 0}</TableCell>
                    <ActionCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      <Dropdown
                        button
                        icon="ellipsis vertical"
                        floating
                        className="icon"
                        direction="left"
                        style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <Dropdown.Menu>
                            <Dropdown.Item
                              text="Edit Team"
                              icon="edit"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                setEditModalTeam(team);
                                setEditModalOpen(true);
                              }}
                            />
                          <Dropdown.Item
                              text="Manage Members"
                              icon="users"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                try { prefetchTeamDetail(team.id); } catch (err) { /* ignore */ }
                                setMembersModalTeamId(team.id);
                                setMembersModalOpen(true);
                              }}
                            />
                          <Dropdown.Item
                            text={team.is_archived ? 'Unarchive' : team.has_snapshot_data ? 'Archive' : 'Delete'}
                            icon={team.is_archived ? 'undo' : team.has_snapshot_data ? 'archive' : 'trash'}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleDelete(team.id);
                            }}
                          />
                        </Dropdown.Menu>
                      </Dropdown>
                    </ActionCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>


          </>
        )}

        {viewMode !== 'org' && (
          <PageSection display="flex" justifyContent="space-between" alignItems="center">
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <SearchInput
                type="text"
                placeholder="Search teams by name..."
                value={nameFilter}
                onChange={(e) => {
                  setNameFilter(e.target.value);
                  setCurrentPage(1);
                }}
                aria-label="Search teams by name"
              />
              <StatusSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'active' | 'archived')}
                aria-label="Filter teams by status"
              >
                <option value="active">Active Teams</option>
                <option value="archived">Archived Teams</option>
              </StatusSelect>
            </div>

            {totalPages > 1 && (
              <Pagination
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={(_, data) => setCurrentPage(data.activePage as number)}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: '‹', icon: true }}
                nextItem={{ content: '›', icon: true }}
              />
            )}
          </PageSection>
        )}
      </PageSection>

      <TeamMembersModal
        key={membersModalOpen ? `team-members-${membersModalTeamId}` : 'team-members-closed'}
        isOpen={membersModalOpen}
        onClose={() => { setMembersModalOpen(false); setMembersModalTeamId(null); }}
        teamId={membersModalTeamId ?? 0}
        onSuccess={() => {
          // refresh teams and current list
          refetch();
          setMembersModalOpen(false);
          setMembersModalTeamId(null);
        }}
      />

      <TeamEditModal
        isOpen={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditModalTeam(null); }}
        team={editModalTeam}
        onSuccess={() => {
          refetch();
          setEditModalOpen(false);
          setEditModalTeam(null);
        }}
      />

      <ImportTeamModal
        open={importTeamModalOpen}
        onClose={() => setImportTeamModalOpen(false)}
      />

      {rebuildConfirmOpen && (
        <PositionedModal open={rebuildConfirmOpen} onClose={() => setRebuildConfirmOpen(false)} size="small">
          <div style={{ padding: 20 }}>
            <h3>Rebuild Team Members</h3>
            <p>
              This will delete all current team memberships and rebuild them from
              employees&apos; <strong>team_external_id</strong> values.
            </p>
            <p>Are you sure you want to continue?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <StyledCancelButton onClick={() => setRebuildConfirmOpen(false)} disabled={isRebuilding} />
              <StyledDeleteButton
                width="180px"
                onClick={confirmRebuildTeamMembers}
                disabled={isRebuilding}
                label={isRebuilding ? 'Rebuilding...' : 'Rebuild Team Members'}
              />
            </div>
          </div>
        </PositionedModal>
      )}

      {deleteConfirmTeam && (
        <PositionedModal open={!!deleteConfirmTeam} onClose={() => setDeleteConfirmTeam(null)} size="small">
          <div style={{ padding: 20 }}>
            <h3>{deleteConfirmTeam.is_archived ? 'Unarchive Team' : deleteConfirmTeam.has_snapshot_data ? 'Archive Team' : 'Delete Team'}</h3>
            {deleteConfirmTeam.is_archived ? (
              <p>
                Restore <strong>{deleteConfirmTeam.name}</strong> to active teams?
              </p>
            ) : deleteConfirmTeam.has_snapshot_data ? (
              <p>
                Archive <strong>{deleteConfirmTeam.name}</strong>? Any reporting teams will become unassigned.
              </p>
            ) : (
              <p>Are you sure you want to delete <strong>{deleteConfirmTeam.name}</strong>? This action cannot be undone.</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <StyledCancelButton onClick={() => setDeleteConfirmTeam(null)} disabled={isMutatingTeam} />
              {deleteConfirmTeam.is_archived ? (
                <StyledArchiveButton width="150px" onClick={confirmDelete} disabled={isMutatingTeam} label={isMutatingTeam ? 'Restoring...' : 'Unarchive Team'} />
              ) : deleteConfirmTeam.has_snapshot_data ? (
                <StyledArchiveButton width="140px" onClick={confirmDelete} disabled={isMutatingTeam} label={isMutatingTeam ? 'Archiving...' : 'Archive Team'} />
              ) : (
                <StyledDeleteButton width="140px" onClick={confirmDelete} disabled={isMutatingTeam} label={isMutatingTeam ? 'Deleting...' : 'Delete Team'} />
              )}
            </div>
          </div>
        </PositionedModal>
      )}
    </PageContent>
  );
};

export default TeamManagement;
